"use client";

import { Container } from "@/components/layout/container";
import { TextShimmer } from "@/components/text-shimmer";
import { BLUR_DATA_URL } from "@/config/site";
import { encodedHotelParams } from "@/feature/hotel/search/lib/hotel-formatter-helper";
import { addDays } from "date-fns";
import { ArrowRight, BedDouble, MapPin, Star } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useCallback, useMemo, useState, useTransition } from "react";
import { EffectCoverflow, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { EdgeFade } from "@/components/edge-fade";

type DestinationCard = {
  id: number;
  title: string;
  description: string;
  img: string;
  region: string;
  hotel: {
    location: {
      id: number;
      name: string;
      code: number;
      type: "Hotel" | "City";
      city_code: number;
      country_code: string;
      country_name: string;
      star_category?: number;
    };
  };
};

const data: DestinationCard[] = [
  {
    id: 77830,
    title: "Dubai",
    description:
      "Modern skyline views, luxury shopping, and a strong base for desert escapes.",
    img: "/images/explore-world/dubai.jpg",
    region: "UNITED ARAB EMIRATES",
    hotel: {
      location: {
        id: 77830,
        name: "Dubai",
        code: 121449,
        type: "City",
        city_code: 121449,
        country_code: "AE",
        country_name: "United Arab Emirates",
        star_category: 0,
      },
    },
  },
  {
    id: 85611,
    title: "London (ON)",
    description:
      "A Canadian city stop with riverfront walks, dining, and easy regional access.",
    img: "/images/explore-world/london-on.jpg",
    region: "CANADA",
    hotel: {
      location: {
        id: 85611,
        name: "London (ON)",
        code: 124944,
        type: "City",
        city_code: 124944,
        country_code: "CA",
        country_name: "Canada",
        star_category: 0,
      },
    },
  },
  {
    id: 142136,
    title: "Mecca/Makkah",
    description:
      "A deeply important destination with spiritual travel demand year-round.",
    img: "/images/explore-world/mecca.jpg",
    region: "SAUDI ARABIA",
    hotel: {
      location: {
        id: 142136,
        name: "Mecca/Makkah",
        code: 135576,
        type: "City",
        city_code: 135576,
        country_code: "SA",
        country_name: "Saudi Arabia",
        star_category: 0,
      },
    },
  },
  {
    id: 142125,
    title: "Jeddah",
    description:
      "Red Sea access, waterfront dining, and a lively gateway to western Saudi Arabia.",
    img: "/images/explore-world/jeddah.jpg",
    region: "SAUDI ARABIA",
    hotel: {
      location: {
        id: 142125,
        name: "Jeddah",
        code: 122945,
        type: "City",
        city_code: 122945,
        country_code: "SA",
        country_name: "Saudi Arabia",
        star_category: 0,
      },
    },
  },
  {
    id: 81785,
    title: "Cox's Bazar",
    description:
      "Long beach views and a relaxed coastal stay that stands out in Bangladesh.",
    img: "/images/explore-world/coxs-bazar.jpg",
    region: "BANGLADESH",
    hotel: {
      location: {
        id: 81785,
        name: "Cox's Bazar",
        code: 156857,
        type: "City",
        city_code: 156857,
        country_code: "BD",
        country_name: "Bangladesh",
        star_category: 0,
      },
    },
  },
  {
    id: 107799,
    title: "Paris",
    description:
      "Classic city breaks with landmarks, cafés, and some of the world's most recognisable streets.",
    img: "/images/explore-world/paris.jpg",
    region: "FRANCE",
    hotel: {
      location: {
        id: 107799,
        name: "Paris",
        code: 124269,
        type: "City",
        city_code: 124269,
        country_code: "FR",
        country_name: "France",
        star_category: 0,
      },
    },
  },
  {
    id: 145046,
    title: "Istanbul",
    description:
      "A bridge between continents with rich history, waterfront views, and constant movement.",
    img: "/images/explore-world/istanbul.jpg",
    region: "TURKEY",
    hotel: {
      location: {
        id: 145046,
        name: "Istanbul",
        code: 124161,
        type: "City",
        city_code: 124161,
        country_code: "TR",
        country_name: "Turkey",
        star_category: 0,
      },
    },
  },
  {
    id: 144548,
    title: "Bangkok",
    description:
      "A high-energy stop for temples, street food, shopping, and easy regional connections.",
    img: "/images/explore-world/bangkok.jpg",
    region: "THAILAND",
    hotel: {
      location: {
        id: 144548,
        name: "Bangkok",
        code: 122861,
        type: "City",
        city_code: 122861,
        country_code: "TH",
        country_name: "Thailand",
        star_category: 0,
      },
    },
  },
  {
    id: 143733,
    title: "Singapore",
    description:
      "Clean city travel with easy transit, skyline dining, and polished family-friendly stays.",
    img: "/images/explore-world/singapore.jpg",
    region: "SINGAPORE",
    hotel: {
      location: {
        id: 143733,
        name: "Singapore",
        code: 121673,
        type: "City",
        city_code: 121673,
        country_code: "SG",
        country_name: "Singapore",
        star_category: 0,
      },
    },
  },
  {
    id: 97657,
    title: "Barcelona",
    description:
      "A coastal city with architecture, food culture, and strong appeal for long weekends.",
    img: "/images/explore-world/barcelona.jpg",
    region: "SPAIN",
    hotel: {
      location: {
        id: 97657,
        name: "Barcelona",
        code: 121242,
        type: "City",
        city_code: 121242,
        country_code: "ES",
        country_name: "Spain",
        star_category: 0,
      },
    },
  },
  {
    id: 81355,
    title: "Sydney",
    description:
      "Harbour views, iconic landmarks, and a strong mix of business and leisure travel.",
    img: "/images/explore-world/sydney.jpg",
    region: "AUSTRALIA",
    hotel: {
      location: {
        id: 81355,
        name: "Sydney",
        code: 124002,
        type: "City",
        city_code: 124002,
        country_code: "AU",
        country_name: "Australia",
        star_category: 0,
      },
    },
  },
  {
    id: 118511,
    title: "Hong Kong",
    description:
      "A dense, energetic destination for skyline views, shopping, food, and short stays.",
    img: "/images/explore-world/hong-kong.jpg",
    region: "HONG KONG",
    hotel: {
      location: {
        id: 118511,
        name: "Hong Kong",
        code: 103850,
        type: "City",
        city_code: 103850,
        country_code: "HK",
        country_name: "Hong Kong",
        star_category: 0,
      },
    },
  },
  {
    id: 120027,
    title: "Bali",
    description:
      "A tropical escape with beaches, temples, and resort stays that work for long breaks.",
    img: "/images/explore-world/bali.jpg",
    region: "INDONESIA",
    hotel: {
      location: {
        id: 120027,
        name: "Bali",
        code: 124781,
        type: "City",
        city_code: 124781,
        country_code: "ID",
        country_name: "Indonesia",
        star_category: 0,
      },
    },
  },
];

const INITIAL_SLIDE_INDEX = Math.floor(data.length / 2);
const STAR_INDEXES = Array.from({ length: 5 }, (_, index) => index);
const EXPLORE_WORLD_SWIPER_MODULES = [EffectCoverflow, Pagination];
const EXPLORE_WORLD_COVERFLOW_EFFECT = {
  rotate: 30,
  stretch: 0,
  depth: 130,
  modifier: 1,
  slideShadows: false,
};

export const ExploreWorld = () => {
  const router = useRouter();
  const [activeCardId, setActiveCardId] = useState<number | null>(null);
  const [isPending, startTransition] = useTransition();

  const defaultSearchDates = useMemo(
    () => ({
      from: addDays(new Date(), 3),
      to: addDays(new Date(), 6),
    }),
    [],
  );

  const getHotelHref = useCallback(
    (item: DestinationCard) => {
      const params = encodedHotelParams({
        date: defaultSearchDates,
        location: item.hotel.location,
        rooms: [
          {
            adults: 2,
            children: 0,
            infants: 0,
            children_ages: [],
          },
        ],
      });

      return `/hotels?${params}`;
    },
    [defaultSearchDates],
  );

  const handleSearchHotel = useCallback(
    (item: DestinationCard) => {
      if (isPending) return;

      const href = getHotelHref(item);
      setActiveCardId(item.id);

      startTransition(() => {
        router.push(href);
      });
    },
    [getHotelHref, isPending, router],
  );

  return (
    <section className="relative z-10  md:pt-20">
      <Container>
        <div className="mb-8 flex flex-col gap-5 md:mb-10 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
              Explore World
            </h2>
            <p className="mt-2 max-w-2xl text-muted-foreground md:text-lg">
              Distinct hotels selected for comfort, location, and memorable
              stays.
            </p>
          </div>

          <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
            <BedDouble className="size-5 text-primary" />
            <div>
              <span className="text-xs font-medium text-muted-foreground">
                Ready for search
              </span>
            </div>
          </div>
        </div>

        <div className="relative min-w-0 overflow-hidden ">
          <EdgeFade edges={["right", "left"]} xSizeClass="w-3" />
          <Swiper
            aria-label="Explore hotel picks"
            centeredSlides
            coverflowEffect={EXPLORE_WORLD_COVERFLOW_EFFECT}
            effect="coverflow"
            grabCursor
            initialSlide={INITIAL_SLIDE_INDEX}
            modules={EXPLORE_WORLD_SWIPER_MODULES}
            observeParents
            observer
            pagination={{ clickable: true, dynamicBullets: true }}
            preventInteractionOnTransition
            resizeObserver
            slidesPerView="auto"
            spaceBetween={18}
            watchSlidesProgress
            className="explore-world-swiper"
          >
            {data.map((item) => {
              const isCardLoading = isPending && activeCardId === item.id;
              const starCount = item.hotel.location.star_category ?? 0;

              return (
                <SwiperSlide
                  key={item.id}
                  className="h-auto! w-[78vw]! max-w-84 sm:w-[20rem]! lg:w-88!"
                >
                  <div
                    onClick={() => handleSearchHotel(item)}
                    aria-busy={isCardLoading}
                    className={`group relative h-104 w-full overflow-hidden rounded-md border border-border bg-card text-left transition-all duration-300 ${
                      isPending
                        ? "cursor-not-allowed pointer-events-none opacity-85"
                        : " hover:-translate-y-1 hover:border-primary/50"
                    }`}
                  >
                    <div className="absolute inset-x-0 top-0 h-[58%] overflow-hidden bg-secondary [clip-path:polygon(0_0,100%_0,100%_84%,0_100%)]">
                      <Image
                        src={item.img}
                        alt={item.title}
                        fill
                        sizes="(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                        className="object-cover transition-transform duration-700 group-hover:scale-110"
                        quality={70}
                        placeholder="blur"
                        blurDataURL={BLUR_DATA_URL}
                        loading="lazy"
                      />

                      <div className="absolute inset-0 bg-linear-to-b from-[#123849]/5 via-transparent to-[#123849]/50" />
                      <div className="absolute left-4 top-4 rounded-sm bg-white/90 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-[#123849] backdrop-blur">
                        {item.region}
                      </div>
                    </div>

                    {isCardLoading && (
                      <div className="absolute inset-0 z-30 grid place-items-center bg-black/40 backdrop-blur-[1px]">
                        <div className="rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs font-semibold text-white">
                          <TextShimmer duration={1.2}>
                            Searching hotels...
                          </TextShimmer>
                        </div>
                      </div>
                    )}

                    <div className="absolute right-4 top-[47%] z-10 flex h-16 w-16 rotate-3 items-center justify-center rounded-md border border-primary/25 bg-background text-primary shadow-[0_12px_30px_-24px_rgba(18,56,73,0.8)] transition-transform duration-300 group-hover:rotate-0">
                      <BedDouble className="size-7" />
                    </div>

                    <div className="absolute inset-x-0 bottom-0 flex h-[45%] flex-col justify-between p-5 pt-7">
                      <div>
                        <div className="mb-2 flex items-center gap-1 text-chart-3">
                          {STAR_INDEXES.map((index) => (
                            <Star
                              key={index}
                              className={`size-3.5 ${
                                index < starCount
                                  ? "fill-current"
                                  : "text-border"
                              }`}
                            />
                          ))}
                        </div>

                        <h3 className="line-clamp-2 text-xl font-extrabold leading-tight tracking-tight text-foreground">
                          {item.title}
                        </h3>

                        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
                          {item.description}
                        </p>
                      </div>

                      <div className="flex items-center justify-between border-t border-dashed border-border pt-3">
                        <span className="inline-flex min-w-0 items-center gap-1.5 text-xs font-bold text-muted-foreground">
                          <MapPin className="size-3.5 shrink-0 text-primary" />
                          <span className="truncate">
                            {item.hotel.location.country_name}
                          </span>
                        </span>
                        <span className="inline-flex items-center gap-1 text-sm font-extrabold text-primary">
                          Search
                          <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </span>
                      </div>
                    </div>

                    <div className="absolute left-0 top-[58%] h-px w-full border-t border-dashed border-border" />
                    <div className="pointer-events-none absolute -left-3 top-[58%] size-6 -translate-y-1/2 rounded-full border border-border bg-background" />
                    <div className="pointer-events-none absolute -right-3 top-[58%] size-6 -translate-y-1/2 rounded-full border border-border bg-background" />
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </Container>
    </section>
  );
};
